/**
 * F-148 §5 `/stock/receiving/drafts/:draftId/review` (AC13–AC16): server preview with
 * `now → +accepted` per variant, totals, the non-blocking similar-receipt warning and a confirm
 * dialog. The post key is created once per `(draftId, version)` in sessionStorage and reused on
 * every retry, so a repeated approval after a timeout returns the original receipt.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { useAuthSession, useTranslation } from '@dukanti/shared';
import {
  usePostReceivingDraft,
  useReceivingAccess,
  useReceivingDraft,
  useReceivingPreview,
} from '@dukanti/shared/hooks';
import type { ReceivingIssue } from '@dukanti/shared/admin';
import { supabase } from '@/lib/supabase';
import { useCurrentStore } from '@/hooks/use-current-store';
import { Button } from '@/components/ui/button';
import {
  PostConfirmDialog,
  ReceivingNotAvailable,
  ReceivingPausedBanner,
  ReferenceBadge,
  ReviewDeltaList,
  SimilarReceiptWarning,
  type PostPhase,
} from '@/features/stock/receiving/components';
import {
  issueMessageKey,
  receivingErrorKey,
  receivingErrorOptions,
  withRawValues,
} from '@/features/stock/receiving/lib/error-messages';
import { idempotencyStore } from '@/features/stock/receiving/lib/idempotency-store';

const CHECKING_AFTER_MS = 4000;

export function ReceivingReviewRoute(): React.JSX.Element {
  const { t, dir } = useTranslation();
  const navigate = useNavigate();
  const { draftId = null } = useParams<{ draftId: string }>();
  const authSession = useAuthSession(supabase);
  const { storeId } = useCurrentStore(authSession);
  const access = useReceivingAccess(supabase, storeId);
  const draftQuery = useReceivingDraft(supabase, draftId);
  const draft = draftQuery.data ?? null;
  const preview = useReceivingPreview(supabase, draftId, draft?.version ?? null);
  const post = usePostReceivingDraft(supabase);

  const canManage = access.data?.canManage ?? false;
  const enabled = access.data?.enabled ?? false;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [phase, setPhase] = useState<PostPhase>('idle');
  const [postError, setPostError] = useState<unknown>(null);

  const postKey = useMemo(
    () =>
      draftId && preview.data
        ? idempotencyStore.getOrCreatePostKey(draftId, preview.data.version)
        : null,
    [draftId, preview.data],
  );

  // Already posted (e.g. reload after success) → go to the receipt.
  useEffect(() => {
    if (draft?.status === 'posted' && draft.postedReceiptId) {
      toast.message(t('admin.receiving.review.alreadyPosted'));
      navigate(`/stock/receiving/receipts/${encodeURIComponent(draft.postedReceiptId)}`, {
        replace: true,
      });
    }
  }, [draft?.status, draft?.postedReceiptId, navigate, t]);

  useEffect(() => {
    if (phase !== 'posting') return;
    const handle = setTimeout(
      () => setPhase((current) => (current === 'posting' ? 'checking' : current)),
      CHECKING_AFTER_MS,
    );
    return () => clearTimeout(handle);
  }, [phase]);

  const doPost = async (): Promise<void> => {
    if (!draftId || !preview.data || !postKey) return;
    setPhase('posting');
    setPostError(null);
    try {
      const result = await post.mutateAsync({
        draftId,
        expectedVersion: preview.data.version,
        reviewToken: preview.data.reviewToken,
        idempotencyKey: postKey,
      });
      idempotencyStore.clearPostKeys(draftId);
      toast.success(
        result.outcome === 'already_posted'
          ? t('admin.receiving.review.alreadyPosted')
          : t('admin.receiving.review.posted'),
      );
      setConfirmOpen(false);
      navigate(`/stock/receiving/receipts/${encodeURIComponent(result.receiptId)}`, {
        replace: true,
      });
    } catch (err) {
      setPostError(err);
      setPhase('idle');
      // Stale review / version conflict / invalid draft: refresh what we show.
      void draftQuery.refetch();
      void preview.refetch();
    }
  };

  const similar = preview.data?.issues.find((issue) => issue.code === 'SIMILAR_RECENT_RECEIPT') as
    | ReceivingIssue<'SIMILAR_RECENT_RECEIPT'>
    | undefined;
  const blocking = preview.data?.issues.filter((issue) => issue.severity === 'blocking') ?? [];
  const draftHref = draftId
    ? `/stock/receiving/drafts/${encodeURIComponent(draftId)}`
    : '/stock/receiving';

  return (
    <main
      dir={dir}
      data-testid="receiving-review-route"
      className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6 lg:px-8"
    >
      <Link
        to={draftHref}
        className="text-muted-foreground hover:text-foreground mb-4 inline-flex min-h-11 items-center gap-1 text-sm"
        data-testid="review-back-to-draft"
      >
        <ArrowLeft aria-hidden="true" className="size-4 rtl:rotate-180" />
        {t('admin.receiving.review.backToDraft')}
      </Link>
      <header className="mb-6 text-start">
        <h1 className="font-display text-foreground text-[28px] font-bold leading-tight tracking-tight">
          {t('admin.receiving.review.title')}
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          {t('admin.receiving.review.description')}
        </p>
        {draft ? (
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
            <bdi className="text-foreground font-medium">{draft.supplier.name}</bdi>
            <ReferenceBadge reference={draft} showHelp={false} />
          </div>
        ) : null}
      </header>

      {access.isLoading || (canManage && (draftQuery.isLoading || preview.isLoading)) ? (
        <div className="space-y-3" data-testid="receiving-review-loading">
          <div className="bg-admin-surface-muted h-40 animate-pulse rounded-lg motion-reduce:animate-none" />
        </div>
      ) : null}

      {/* A failed access request is transient: never treated as "no permission" or "paused". */}
      {access.isError ? (
        <div
          role="alert"
          className="border-border bg-card shadow-admin-card rounded-lg border p-6 text-center"
          data-testid="receiving-access-error"
        >
          <p className="text-muted-foreground mb-4 text-sm">{t('common.errors.networkFailed')}</p>
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            onClick={() => void access.refetch()}
            data-testid="receiving-access-retry"
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {access.data && !canManage ? <ReceivingNotAvailable /> : null}
      {access.data && canManage && !enabled ? <ReceivingPausedBanner draftsReadOnly /> : null}

      {access.data && canManage && (draftQuery.isError || preview.isError) ? (
        <div
          role="alert"
          className="border-border bg-card shadow-admin-card rounded-lg border p-6 text-center"
          data-testid="receiving-review-error"
        >
          <p className="text-muted-foreground mb-4 text-sm">
            {t(
              receivingErrorKey(draftQuery.error ?? preview.error),
              receivingErrorOptions(draftQuery.error ?? preview.error),
            )}
          </p>
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            onClick={() => {
              void draftQuery.refetch();
              void preview.refetch();
            }}
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {access.data && canManage && preview.data && draft?.status === 'open' ? (
        <div className="space-y-5">
          {blocking.length > 0 ? (
            <section
              role="alert"
              className="border-destructive/40 bg-destructive/10 rounded-lg border p-4 text-start"
              data-testid="review-blocking"
            >
              <p className="text-destructive text-sm font-semibold">
                {t('admin.receiving.review.notReady')}
              </p>
              <ul className="mt-2 space-y-1 text-sm">
                {blocking.map((issue, index) => (
                  <li
                    key={`${issue.code}-${issue.lineNo ?? ''}-${index}`}
                    className="text-destructive"
                    data-code={issue.code}
                  >
                    {issue.lineNo
                      ? t(
                          'admin.receiving.draft.errorSummaryItem',
                          withRawValues({
                            line: issue.lineNo,
                            message: t(issueMessageKey(issue.code), issue.meta),
                          }),
                        )
                      : t(issueMessageKey(issue.code), issue.meta)}
                  </li>
                ))}
              </ul>
              <Link
                to={draftHref}
                className="text-foreground mt-3 inline-flex min-h-11 items-center underline underline-offset-4"
              >
                {t('admin.receiving.review.backToDraft')}
              </Link>
            </section>
          ) : null}

          {similar ? <SimilarReceiptWarning issue={similar} /> : null}

          <ReviewDeltaList deltas={preview.data.deltas} totals={preview.data.totals} />

          {postError !== null && postError !== undefined && !confirmOpen ? (
            <p
              role="alert"
              className="text-destructive text-start text-sm"
              data-testid="review-post-error"
            >
              {t(receivingErrorKey(postError), receivingErrorOptions(postError))}
            </p>
          ) : null}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              onClick={() => navigate(draftHref)}
            >
              {t('admin.receiving.review.backToDraft')}
            </Button>
            <Button
              type="button"
              className="min-h-11"
              disabled={!enabled || !preview.data.ready || phase !== 'idle'}
              onClick={() => {
                setPostError(null);
                setConfirmOpen(true);
              }}
              data-testid="review-open-confirm"
            >
              {t('admin.receiving.review.confirm')}
            </Button>
          </div>

          <PostConfirmDialog
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            lineCount={preview.data.totals.lines}
            acceptedUnits={preview.data.totals.accepted}
            phase={phase}
            error={postError}
            onConfirm={() => void doPost()}
            onRetry={() => void doPost()}
          />
        </div>
      ) : null}
    </main>
  );
}
